/**
 * ChatBubble — one message in the look of the Home chat (RunDrawer): your own message is a filled bubble, everyone else's
 * (people, agents, your own Lyran) is a card with a small avatar. Presentation only, so team conversations can share the
 * Home chat's style without sharing its single-user run engine.
 */
import React from 'react';
import { renderChatMarkdown } from '../../utils/chatMarkdown.js';

const T = {
  surface: 'var(--lyra-color-surface)', border: 'var(--lyra-color-border)', ink: 'var(--lyra-color-text)', ink3: 'var(--lyra-color-text-muted)',
  ink4: 'var(--app-text-3)', accent: 'var(--lyra-color-primary)', accentLight: 'var(--app-primary-soft)', accentMid: 'var(--app-primary-soft-2)',
};

export default function ChatBubble({
  align = 'left',        // which side the message sits on
  filled = false,        // true: the signed-in person's own typed message (accent bubble, plain text)
  name = '',             // sender, shown above a card
  via = '',              // a Lyran that answered through one of its owner's agents
  paidBy = '',           // someone other than the agent's owner covered the tokens (a requester or sponsor)
  showName = true,
  content = '',
  time = '',
  attachments = null,    // node rendered above the bubble
}) {
  const right = align === 'right';
  const wrap = { display: 'flex', flexDirection: 'column', alignItems: right ? 'flex-end' : 'flex-start', marginBottom: 14 };
  const clock = time ? <div style={{ fontSize: 10, color: T.ink4, marginTop: 4, paddingInline: 2 }}>{time}</div> : null;

  if (filled) {
    return (
      <div style={wrap}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, maxWidth: '82%' }}>
          {attachments}
          {content && (
            <div style={{ padding: '10px 16px', borderRadius: '18px 18px 5px 18px', background: T.accent, color: 'var(--app-on-accent)', fontSize: 13, lineHeight: 1.7, wordBreak: 'break-word' }}>
              <span style={{ whiteSpace: 'pre-wrap' }}>{content}</span>
            </div>
          )}
        </div>
        {clock}
      </div>
    );
  }

  const initial = (name || '?').trim()[0]?.toUpperCase() || '?';
  return (
    <div style={wrap}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, maxWidth: '92%', flexDirection: right ? 'row-reverse' : 'row' }}>
        <div aria-hidden style={{ width: 26, height: 26, borderRadius: 8, background: T.accentLight, border: `1px solid ${T.accentMid}`, color: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
          {initial}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: right ? 'flex-end' : 'flex-start', gap: 6 }}>
          {showName && name && <div style={{ fontSize: 11, fontWeight: 700, color: T.ink3, paddingInline: 2 }}>{name}{via && <span style={{ fontWeight: 500, color: T.ink4 }}> · via {via}</span>}{paidBy && <span style={{ fontWeight: 500, color: T.ink4 }}> · tokens by {paidBy}</span>}</div>}
          {attachments}
          {content && (
            <div
              style={{ padding: '10px 15px', borderRadius: right ? '18px 6px 18px 18px' : '6px 18px 18px 18px', background: T.surface, border: `0.5px solid ${T.border}`, [right ? 'borderRight' : 'borderLeft']: `2px solid ${T.accentMid}`, color: T.ink, fontSize: 13, lineHeight: 1.7, wordBreak: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: renderChatMarkdown(content) }}
            />
          )}
        </div>
      </div>
      {clock}
    </div>
  );
}
